import React, { useContext } from "react";
import { IntlContext } from "context";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import L from "leaflet";
import { Shield, MapPin } from "lucide-react";

const MAP_MARKER_ICON = L.divIcon({
    html: `
        <div style="position:relative;width:48px;height:60px">
            <div style="width:48px;height:48px;background:#4c4cf4;border-radius:50%;display:flex;align-items:center;justify-content:center;filter:drop-shadow(0px 8px 5px rgba(0,0,0,.1)) drop-shadow(0px 20px 12.5px rgba(0,0,0,0.1))">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill-rule="evenodd"/>
                </svg>
            </div>
            <div style="position:absolute;left:18px;top:46px;width:12px;height:12px;background:#4c4cf4;border-radius:50%;opacity:0.3;filter:blur(4px)"></div>
        </div>
    `,
    className: "",
    iconSize: [48, 60],
    iconAnchor: [24, 24],
    popupAnchor: [0, -28],
});

type Props = {
    lat: number;
    lon: number;
    locationLabel: string;
};

const AdLocationCard: React.FC<Props> = ({ lat, lon, locationLabel }) => {
    const intl = useContext(IntlContext);
    const hasValidCoordinates = Number.isFinite(lat) && Number.isFinite(lon);

    return (
        <div className="bg-white rounded-[32px] border border-[#f1f5f9] drop-shadow-[0px_10px_20px_rgba(0,0,0,0.04)] pt-8 px-8 pb-8 flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <div className="w-1 h-4 bg-[#4c4cf4] rounded-full shrink-0" />
                <h2 className="text-[14px] font-black tracking-[2.1px] uppercase text-[#1a1c2d]">
                    {intl.formatMessage("ad.view.location")}
                </h2>
            </div>

            <div className="relative w-full h-[400px] rounded-[24px] overflow-hidden border-2 border-[#f1f5f9]">
                {hasValidCoordinates ? (
                    <MapContainer
                        center={[lat, lon]}
                        zoom={13}
                        scrollWheelZoom={false}
                        style={{ width: "100%", height: "100%" }}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Circle
                            center={[lat, lon]}
                            radius={700}
                            pathOptions={{
                                color: "#4c4cf4",
                                fillColor: "#4c4cf4",
                                fillOpacity: 0.12,
                                weight: 1.5,
                            }}
                        />
                        <Marker position={[lat, lon]} icon={MAP_MARKER_ICON}>
                            <Popup>{locationLabel}</Popup>
                        </Marker>
                    </MapContainer>
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-100 via-emerald-50 to-blue-50 flex items-center justify-center">
                        <span className="italic text-gray-500">
                            {intl.formatMessage("ad.view.location_unavailable")}
                        </span>
                    </div>
                )}
                {hasValidCoordinates && (
                    <div className="absolute bottom-[22px] left-[22px] right-[22px] bg-[rgba(255,255,255,0.95)] border border-[#f1f5f9] rounded-[20px] shadow-[0px_20px_25px_0px_rgba(0,0,0,0.1),0px_8px_10px_0px_rgba(0,0,0,0.1)] py-[17px] px-[17px] flex items-center gap-3 z-[400]">
                        <div className="bg-[#eef2ff] rounded-[14px] w-10 h-10 flex items-center justify-center shrink-0">
                            <MapPin className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <div className="text-xs font-black text-gray-900 leading-5">{locationLabel}</div>
                            <div className="text-xs font-bold text-slate-400 leading-4">
                                {intl.formatMessage("ad.view.location_approximate_zone")}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="bg-indigo-50 border border-indigo-100 rounded-[20px] py-[17px] px-[17px] flex items-center gap-3">
                <Shield className="shrink-0 text-primary" />
                <p className="text-[13px] text-indigo-900">
                    <span className="font-black">{intl.formatMessage("ad.view.privacy_label")} </span>
                    <span className="font-medium">{intl.formatMessage("ad.view.privacy_notice")}</span>
                </p>
            </div>
        </div>
    );
};

export default AdLocationCard;
